import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MetricsChart } from '../../components/observability/MetricsChart';
import { observabilityService, type MetricResult } from '../../services/observability.service';
import { RefreshCw, Clock, Shield, Activity, Cpu, AlertTriangle, Ban, CheckCircle, ExternalLink } from 'lucide-react';
import { useSettingsStore } from '../../stores/settingsStore';
import { formatTimeOnly } from '../../utils/formatDate';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import FeatureDisabledBanner from '../../components/common/FeatureDisabledBanner';
import EdgeSelector from '../../components/common/EdgeSelector';

const SuricataMonitoring: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const edgeIdParam = searchParams.get('edgeId');

  const { getDateFormatSettings, isFeatureEnabled } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [selectedEdgeId, setSelectedEdgeId] = useState<string>(edgeIdParam || '');

  // Status Metrics
  const [enabled, setEnabled] = useState<number | null>(null);
  const [mode, setMode] = useState<string | null>(null);
  const [uptime, setUptime] = useState<number | null>(null);
  const [threads, setThreads] = useState<number | null>(null);
  const [memoryUsed, setMemoryUsed] = useState<number | null>(null);
  const [flowsActive, setFlowsActive] = useState<number | null>(null);

  // Time range
  const [timeRange, setTimeRange] = useState<number>(3600); // seconds

  // Alert Metrics
  const [totalAlerts, setTotalAlerts] = useState<number | null>(null);
  const [blockedAlerts, setBlockedAlerts] = useState<number | null>(null);
  const [allowedAlerts, setAllowedAlerts] = useState<number | null>(null);
  const [alertsBySeverity, setAlertsBySeverity] = useState<{ name: string; value: number }[]>([]);

  // Historical Data
  const [alertRateData, setAlertRateData] = useState<any[]>([]);
  const [packetsData, setPacketsData] = useState<any[]>([]);
  const [throughputData, setThroughputData] = useState<any[]>([]);
  const [protocolData, setProtocolData] = useState<any[]>([]);
  const [flowsData, setFlowsData] = useState<any[]>([]);
  const [appLayerData, setAppLayerData] = useState<any[]>([]);

  const fetchData = async (isBackground = false) => {
    if (!selectedEdgeId) return;

    if (!isBackground) setLoading(true);
    try {
      const options = isBackground ? { showLoading: false } : undefined;
      const end = Math.floor(Date.now() / 1000);
      const start = end - timeRange;
      const step = timeRange <= 3600 ? 60 : timeRange <= 21600 ? 120 : 300;

      // Status queries (range for lastNotNull)
      const statusStart = start;
      const statusStep = 60;

      const [
        enabledRes,
        modeRes,
        uptimeRes,
        threadsRes,
        memoryRes,
        flowsActiveRes,
        alertsTotalRes,
        alertsBlockedRes,
        alertsAllowedRes,
        alertsBySeverityRes,
        alertRateRes,
        packetsReceivedRes,
        packetsDroppedRes,
        throughputRes,
        protocolRes,
        flowsNewRes,
        flowsTimeoutRes,
        appLayerRes,
      ] = await Promise.all([
        // Status metrics
        observabilityService.queryRange(
          `edge_suricata_enabled{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `edge_suricata_mode{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `edge_suricata_uptime_seconds{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `edge_suricata_threads_active{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `edge_suricata_memory_used_bytes{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `edge_suricata_flows_active{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        // Alert summary
        observabilityService.queryRange(
          `sum(edge_suricata_alerts_total{edge_id="${selectedEdgeId}"})`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `edge_suricata_alerts_blocked_total{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `edge_suricata_alerts_allowed_total{edge_id="${selectedEdgeId}"}`,
          statusStart, end, statusStep, options
        ),
        observabilityService.queryRange(
          `sum by (severity) (edge_suricata_alerts_total{edge_id="${selectedEdgeId}"})`,
          statusStart, end, statusStep, options
        ),
        // Historical charts
        observabilityService.queryRange(
          `sum by (severity) (rate(edge_suricata_alerts_total{edge_id="${selectedEdgeId}"}[5m]))`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `rate(edge_suricata_packets_received_total{edge_id="${selectedEdgeId}"}[5m])`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `rate(edge_suricata_packets_dropped_total{edge_id="${selectedEdgeId}"}[5m])`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `rate(edge_suricata_bytes_received_total{edge_id="${selectedEdgeId}"}[5m])`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `sum by (protocol) (rate(edge_suricata_protocol_decoded_total{edge_id="${selectedEdgeId}"}[5m]))`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `rate(edge_suricata_flows_total{edge_id="${selectedEdgeId}"}[5m])`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `rate(edge_suricata_flows_timeout_total{edge_id="${selectedEdgeId}"}[5m])`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `sum by (protocol) (rate(edge_suricata_app_layer_tx_total{edge_id="${selectedEdgeId}"}[5m]))`,
          start, end, step, options
        ),
      ]);

      // Process status metrics
      if (enabledRes.status === 'success') {
        const value = getLastNotNull(enabledRes.data.result);
        if (value !== null) setEnabled(value);
      }
      if (modeRes.status === 'success') {
        const modeLabel = getModeFromResult(modeRes.data.result);
        setMode(modeLabel);
      }
      if (uptimeRes.status === 'success') {
        const value = getLastNotNull(uptimeRes.data.result);
        if (value !== null) setUptime(value);
      }
      if (threadsRes.status === 'success') {
        const value = getLastNotNull(threadsRes.data.result);
        if (value !== null) setThreads(value);
      }
      if (memoryRes.status === 'success') {
        const value = getLastNotNull(memoryRes.data.result);
        if (value !== null) setMemoryUsed(value);
      }
      if (flowsActiveRes.status === 'success') {
        const value = getLastNotNull(flowsActiveRes.data.result);
        if (value !== null) setFlowsActive(value);
      }

      // Process alert summary
      if (alertsTotalRes.status === 'success') {
        const value = getLastNotNull(alertsTotalRes.data.result);
        if (value !== null) setTotalAlerts(value);
      }
      if (alertsBlockedRes.status === 'success') {
        const value = getLastNotNull(alertsBlockedRes.data.result);
        if (value !== null) setBlockedAlerts(value);
      }
      if (alertsAllowedRes.status === 'success') {
        const value = getLastNotNull(alertsAllowedRes.data.result);
        if (value !== null) setAllowedAlerts(value);
      }
      if (alertsBySeverityRes.status === 'success') {
        setAlertsBySeverity(extractSeverityData(alertsBySeverityRes.data.result));
      }

      // Process historical data
      if (alertRateRes.status === 'success') {
        setAlertRateData(transformMetricDataBySeverity(alertRateRes.data.result));
      }

      // Combine packets received and dropped
      const packetsResults = [
        ...(packetsReceivedRes.status === 'success'
          ? packetsReceivedRes.data.result.map((r: any) => ({ ...r, metric: { ...r.metric, type: 'Received' } }))
          : []),
        ...(packetsDroppedRes.status === 'success'
          ? packetsDroppedRes.data.result.map((r: any) => ({ ...r, metric: { ...r.metric, type: 'Dropped' } }))
          : []),
      ];
      setPacketsData(transformMetricDataByType(packetsResults));

      if (throughputRes.status === 'success') {
        setThroughputData(transformMetricData(throughputRes.data.result, 'throughput'));
      }

      if (protocolRes.status === 'success') {
        setProtocolData(transformMetricDataByProtocol(protocolRes.data.result));
      }

      // Combine flows new and timeout
      const flowsResults = [
        ...(flowsNewRes.status === 'success'
          ? flowsNewRes.data.result.map((r: any) => ({ ...r, metric: { ...r.metric, type: 'New Flows' } }))
          : []),
        ...(flowsTimeoutRes.status === 'success'
          ? flowsTimeoutRes.data.result.map((r: any) => ({ ...r, metric: { ...r.metric, type: 'Timeouts' } }))
          : []),
      ];
      setFlowsData(transformMetricDataByType(flowsResults));

      if (appLayerRes.status === 'success') {
        setAppLayerData(transformMetricDataByProtocol(appLayerRes.data.result));
      }

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to fetch Suricata metrics:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
    const interval = setInterval(() => fetchData(true), 30000); // 30s refresh
    return () => clearInterval(interval);
  }, [selectedEdgeId, timeRange]);

  // Helper to get last non-null value from range query
  const getLastNotNull = (result: MetricResult[]): number | null => {
    if (!result || result.length === 0) return null;

    let lastValue: number | null = null;
    let lastTimestamp = 0;

    for (const series of result) {
      if (series.values && series.values.length > 0) {
        const lastPoint = series.values[series.values.length - 1];
        if (lastPoint && lastPoint[1] !== 'NaN' && lastPoint[1] !== '') {
          const timestamp = lastPoint[0];
          const value = parseFloat(lastPoint[1]);
          if (!isNaN(value) && timestamp > lastTimestamp) {
            lastValue = value;
            lastTimestamp = timestamp;
          }
        }
      }
    }
    return lastValue;
  };

  // Extract mode label from result
  const getModeFromResult = (result: MetricResult[]): string | null => {
    if (!result || result.length === 0) return null;

    for (const series of result) {
      if (series.values && series.values.length > 0) {
        const lastPoint = series.values[series.values.length - 1];
        if (lastPoint && parseFloat(lastPoint[1]) === 1) {
          return series.metric.mode || 'unknown';
        }
      }
    }
    return null;
  };

  // Extract severity data for pie chart
  const extractSeverityData = (result: MetricResult[]): { name: string; value: number }[] => {
    const data: { name: string; value: number }[] = [];

    for (const series of result) {
      const severity = series.metric.severity || 'unknown';
      const value = getLastNotNull([series]);
      if (value !== null && value > 0) {
        data.push({ name: severity.charAt(0).toUpperCase() + severity.slice(1), value });
      }
    }

    return data;
  };

  // Transform metric data helpers
  const transformMetricData = (results: MetricResult[], keyName: string) => {
    if (!results || results.length === 0) return [];

    const dataMap = new Map<number, any>();

    results.forEach((series) => {
      series.values?.forEach(([ts, val]) => {
        if (!dataMap.has(ts)) {
          dataMap.set(ts, { timestamp: ts });
        }
        const point = dataMap.get(ts);
        point[keyName] = parseFloat(val);
      });
    });

    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  const transformMetricDataBySeverity = (results: MetricResult[]) => {
    if (!results || results.length === 0) return [];

    const dataMap = new Map<number, any>();

    results.forEach((series) => {
      const severity = series.metric.severity || 'unknown';
      const label = severity.charAt(0).toUpperCase() + severity.slice(1);

      series.values?.forEach(([ts, val]) => {
        if (!dataMap.has(ts)) {
          dataMap.set(ts, { timestamp: ts });
        }
        const point = dataMap.get(ts);
        point[label] = parseFloat(val);
      });
    });

    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  const transformMetricDataByType = (results: MetricResult[]) => {
    if (!results || results.length === 0) return [];

    const dataMap = new Map<number, any>();

    results.forEach((series) => {
      const type = series.metric.type || 'value';

      series.values?.forEach(([ts, val]) => {
        if (!dataMap.has(ts)) {
          dataMap.set(ts, { timestamp: ts });
        }
        const point = dataMap.get(ts);
        point[type] = parseFloat(val);
      });
    });

    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  const transformMetricDataByProtocol = (results: MetricResult[]) => {
    if (!results || results.length === 0) return [];

    const dataMap = new Map<number, any>();

    results.forEach((series) => {
      const protocol = series.metric.protocol || 'unknown';
      const label = protocol.toUpperCase();

      series.values?.forEach(([ts, val]) => {
        if (!dataMap.has(ts)) {
          dataMap.set(ts, { timestamp: ts });
        }
        const point = dataMap.get(ts);
        point[label] = parseFloat(val);
      });
    });

    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  // Format helpers
  const formatUptime = (seconds: number | null) => {
    if (seconds === null) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatBytes = (bytes: number | null) => {
    if (bytes === null) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatNumber = (num: number | null) => {
    if (num === null) return 'N/A';
    return num.toLocaleString();
  };

  // Colors
  const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
  const SEVERITY_COLORS: Record<string, string> = {
    High: '#ef4444',
    Medium: '#f59e0b',
    Low: '#10b981',
  };

  // Dynamic lines helper
  const getDynamicLines = (data: any[], excludeKeys = ['timestamp']) => {
    if (!data || data.length === 0) return [];
    const keys = Object.keys(data[0] || {}).filter(k => !excludeKeys.includes(k));
    return keys.map((k, i) => ({
      dataKey: k,
      name: k,
      color: SEVERITY_COLORS[k] || COLORS[i % COLORS.length]
    }));
  };

  // Feature flag gate
  if (!isFeatureEnabled('suricata')) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Suricata IDS/IPS Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">Intrusion detection and prevention metrics</p>
        </div>
        <FeatureDisabledBanner featureKey="suricata" featureName="Suricata IDS/IPS" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Suricata IDS/IPS Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">Intrusion detection and prevention metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500 hidden sm:flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            {formatTimeOnly(lastRefreshed, dateSettings)}
          </span>
          <button
            onClick={() => fetchData(false)}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Edge and Time Range Selection */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Edge Device</label>
          <EdgeSelector
            selectedEdgeId={selectedEdgeId}
            onEdgeChange={setSelectedEdgeId}
          />
        </div>
        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value={3600}>Last 1 hour</option>
            <option value={21600}>Last 6 hours</option>
            <option value={86400}>Last 24 hours</option>
            <option value={604800}>Last 7 days</option>
          </select>
        </div>
        {selectedEdgeId && (
          <div className="pt-6">
            <button
              onClick={() => navigate(`/edges/${selectedEdgeId}?tab=alerts`)}
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View Alerts</span>
            </button>
          </div>
        )}
      </div>

      {/* Status Cards Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Enabled Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Status</h3>
            <Shield className="h-5 w-5 text-gray-400" />
          </div>
          <div className={`mt-2 text-lg font-semibold flex items-center ${enabled === 1 ? 'text-green-600' : 'text-red-600'}`}>
            <span className={`h-3 w-3 rounded-full mr-2 ${enabled === 1 ? 'bg-green-500' : 'bg-red-500'}`}></span>
            {enabled === 1 ? 'Enabled' : 'Disabled'}
          </div>
        </div>

        {/* Mode */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Mode</h3>
            <Activity className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900 uppercase">
            {mode || 'N/A'}
          </p>
        </div>

        {/* Uptime */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Uptime</h3>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{formatUptime(uptime)}</p>
        </div>

        {/* Threads */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Active Threads</h3>
            <Cpu className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{threads ?? 'N/A'}</p>
        </div>
      </div>

      {/* Status Cards Row 2 + Alert Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Resource Stats */}
        <div className="grid grid-cols-2 gap-4">
          {/* Memory */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500">Memory</h3>
            <p className="mt-1 text-xl font-semibold text-gray-900">{formatBytes(memoryUsed)}</p>
          </div>

          {/* Active Flows */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500">Active Flows</h3>
            <p className="mt-1 text-xl font-semibold text-gray-900">{formatNumber(flowsActive)}</p>
          </div>

          {/* Blocked */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Blocked</h3>
              <Ban className="h-4 w-4 text-red-500" />
            </div>
            <p className="mt-1 text-xl font-semibold text-red-600">{formatNumber(blockedAlerts)}</p>
          </div>

          {/* Allowed */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Allowed</h3>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </div>
            <p className="mt-1 text-xl font-semibold text-green-600">{formatNumber(allowedAlerts)}</p>
          </div>
        </div>

        {/* Total Alerts */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Total Alerts (Cumulative)</h3>
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          </div>
          <p className="text-4xl font-bold text-gray-900">{formatNumber(totalAlerts)}</p>
          <p className="text-sm text-gray-500 mt-2">Since Suricata started</p>
        </div>

        {/* Alerts by Severity Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Alerts by Severity</h3>
          {alertsBySeverity.length > 0 ? (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={alertsBySeverity}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {alertsBySeverity.map((entry, index) => (
                      <Cell key={entry.name} fill={SEVERITY_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-500">
              No alert data available
            </div>
          )}
        </div>
      </div>

      {/* Charts Row 1: Alert Rate + Packet Processing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="Alert Rate by Severity"
          data={alertRateData}
          loading={loading}
          unit=" /s"
          lines={getDynamicLines(alertRateData)}
        />

        <MetricsChart
          title="Packet Processing Rate"
          data={packetsData}
          loading={loading}
          unit=" pkt/s"
          lines={getDynamicLines(packetsData)}
        />
      </div>

      {/* Charts Row 2: Throughput + Traffic by Protocol */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="Throughput (Bytes/sec)"
          data={throughputData}
          loading={loading}
          unit=" B/s"
          lines={[{ dataKey: 'throughput', name: 'Throughput', color: COLORS[0] }]}
        />

        <MetricsChart
          title="Traffic by Protocol"
          data={protocolData}
          loading={loading}
          unit=" pkt/s"
          lines={getDynamicLines(protocolData)}
        />
      </div>

      {/* Charts Row 3: Flow Statistics + App Layer Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="Flow Statistics"
          data={flowsData}
          loading={loading}
          unit=" /s"
          lines={getDynamicLines(flowsData)}
        />

        <MetricsChart
          title="App Layer Transactions"
          data={appLayerData}
          loading={loading}
          unit=" tx/s"
          lines={getDynamicLines(appLayerData)}
        />
      </div>
    </div>
  );
};

export default SuricataMonitoring;
